import { Avatar, Button, EmptyState, Spinner, cn } from '@nexo/ui';
import {
  Activity,
  ArrowLeft,
  TriangleAlert,
  Building2,
  LayoutDashboard,
  MessageSquareWarning,
  Package,
  ServerCog,
  Shield,
  ToggleRight,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, Outlet } from 'react-router';
import { LogoMark } from '../../components/Logo';
import { formatNumber } from '../../lib/format';
import { useMe } from '../auth/AuthProvider';
import { useNewFeedbackCount } from './AdminFeedback';
import { errorMessage } from '../../lib/forms';

const nav: { to: string; label: string; icon: ReactNode; end?: boolean }[] = [
  { to: '/admin', label: 'Resumen', icon: <LayoutDashboard />, end: true },
  { to: '/admin/users', label: 'Usuarios', icon: <Users /> },
  { to: '/admin/teams', label: 'Equipos', icon: <Building2 /> },
  { to: '/admin/feedback', label: 'Reportes', icon: <MessageSquareWarning /> },
  { to: '/admin/releases', label: 'Versiones', icon: <Package /> },
  { to: '/admin/features', label: 'Funciones', icon: <ToggleRight /> },
  { to: '/admin/audit', label: 'Actividad', icon: <Activity /> },
  { to: '/admin/system', label: 'Sistema', icon: <ServerCog /> },
];

/** Platform administration area (only for platform admins). */
export function AdminLayout() {
  const { t } = useTranslation();
  const me = useMe();
  if (!me.isPlatformAdmin) return <Navigate to="/app" replace />;

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="flex h-[3.25rem] shrink-0 items-center gap-3 bg-ink px-4 text-ink-fg">
        <Link to="/app" className="flex items-center gap-2" aria-label={t('Volver a la app')}>
          <LogoMark className="size-7" />
          <span className="text-[17px] font-semibold tracking-tight max-sm:hidden">nexo</span>
        </Link>
        <span className="h-5 w-px bg-white/15" />
        <span className="flex items-center gap-2 text-[15px] font-semibold">
          <Shield className="size-4 text-highlight" /> {t('Administración')}
        </span>
        <span className="flex-1" />
        <Link
          to="/app"
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] text-ink-muted hover:bg-white/10 hover:text-ink-fg"
        >
          <ArrowLeft className="size-4" />{' '}
          <span className="max-sm:hidden">{t('Volver a la app')}</span>
        </Link>
        <Avatar id={me.id} name={me.displayName} src={me.avatarUrl} size="sm" />
      </header>
      <div className="flex min-h-0 flex-1 flex-col md:flex-row md:gap-2 md:p-2 md:pl-0">
        <nav
          aria-label={t('Administración')}
          className="flex shrink-0 gap-1 overflow-x-auto px-2 py-2 md:w-52 md:flex-col md:overflow-visible md:px-2.5 md:py-1"
        >
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex h-9 shrink-0 items-center gap-2.5 rounded-lg px-3 text-sm whitespace-nowrap transition-colors [&_svg]:size-4',
                  isActive
                    ? 'bg-selected font-medium text-selected-fg shadow-panel'
                    : 'text-muted hover:bg-surface hover:text-text',
                )
              }
            >
              {item.icon}
              {t(item.label)}
              {item.to === '/admin/feedback' && <NewCount />}
            </NavLink>
          ))}
        </nav>
        <main className="scrollbar-thin min-h-0 flex-1 overflow-y-auto bg-surface md:rounded-2xl md:border md:border-border md:shadow-panel">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NewCount() {
  const count = useNewFeedbackCount();
  if (!count) return null;
  return (
    <span className="ml-auto rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-fg tabular-nums">
      {count}
    </span>
  );
}

export function AdminPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-muted">
      <span>
        {t('{{total}} resultados · página {{page}} de {{pages}}', {
          total: formatNumber(total),
          page,
          pages,
        })}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="rounded-lg px-3 py-1.5 ring-1 ring-border hover:bg-surface-2 disabled:opacity-40"
        >
          {t('Anterior')}
        </button>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="rounded-lg px-3 py-1.5 ring-1 ring-border hover:bg-surface-2 disabled:opacity-40"
        >
          {t('Siguiente')}
        </button>
      </div>
    </div>
  );
}

/** Table wrapper with consistent styling; horizontally scrollable on small screens. */
export function DataTable({
  head,
  children,
  empty,
}: {
  head: string[];
  children: ReactNode;
  empty?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="scrollbar-thin overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-surface-2/70 text-left text-[11px] font-semibold tracking-wider text-subtle uppercase">
          <tr>
            {head.map((h, i) => (
              <th key={`${h}-${i}`} className="px-4 py-2.5 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
      {empty && <p className="px-4 py-10 text-center text-sm text-subtle">{t('Sin resultados')}</p>}
    </div>
  );
}

/** Loading / error states for admin queries (never an endless spinner). */
export function QueryState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  if (!error) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }
  return (
    <EmptyState
      icon={<TriangleAlert className="size-8 text-warning" />}
      title={t('No se pudieron cargar los datos')}
      description={errorMessage(error)}
      action={
        <Button variant="secondary" onClick={onRetry}>
          {t('Reintentar')}
        </Button>
      }
    />
  );
}
